import React, { MouseEvent, useRef, useState, useCallback } from "react";
import { UiModal, IPropsUiModalBase, EUiModalReason } from "../../../shared/components/ui/UiModal/UiModal";
import { Button } from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import "./ModalImportLVL1.scss";
import DragAndDrop from "../../components/DragAndDrop/DragAndDrop";
import StatusAlert from "./StatusAlert";
import { getAlertProps } from "./statusAlert.config";
import FooterModalImportLVL1 from "./FooterModalImportLVL1";
import {
    TAddDuplicateEventsResponse,
    TDuplicateEvent,
    TImportEventResponse,
} from "../../../shared/services/massImportLVL1/massImportService";
import ImportedDataDisplay from "./ImportedDataDisplay";
import { useNavigate } from "react-router-dom";

interface IModalImportLVL1Props extends IPropsUiModalBase {
    onImport: (file: File) => Promise<TImportEventResponse>;
    onDuplicateSelection: (
        importedFileId: number,
        selectedEvents: TDuplicateEvent[]
    ) => Promise<TAddDuplicateEventsResponse>;
}

export type TImportState =
    | {
          type: "import";
          response: TImportEventResponse;
      }
    | {
          type: "duplicate";
          response: TAddDuplicateEventsResponse;
      }
    | null;

const ModalImportLVL1: React.FC<IModalImportLVL1Props> = ({
    onImport,
    onDuplicateSelection,
    onClose,
    open,
    ...rest
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [importState, setImportState] = useState<TImportState>(null);
    const [selectedDuplicates, setSelectedDuplicates] = useState<TDuplicateEvent[]>([]);
    const [hasImportableEvents, setHasImportableEvents] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    const handleImport = async (file: File) => {
        setIsLoading(true);
        setError(undefined);

        try {
            const response = await onImport(file);
            console.log("Import Response:", response);
            setImportState({ type: "import", response });
            // Update the state to reflect if there are importable events
            setHasImportableEvents(response.totalImportedEvents > 0);
        } catch (error) {
            console.error("Error during import:", error);
            setError("An error occurred during import. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDuplicateSelectionChange = useCallback((selectedEvents: TDuplicateEvent[]) => {
        setSelectedDuplicates(selectedEvents);
    }, []);

    const handleDuplicateSubmit = useCallback(async () => {
        // Allow submission if there are imported events, even if no duplicates are selected.
        if (
            (selectedDuplicates.length === 0 && !hasImportableEvents) ||
            !importState?.response.duplicateEventsWithFile.importedFileId
        )
            return;

        setIsLoading(true);
        setError(undefined);

        try {
            const response = await onDuplicateSelection(
                importState.response.duplicateEventsWithFile.importedFileId,
                selectedDuplicates
            );
            console.log("Duplicate events added:", response);
            setImportState({ type: "duplicate", response });
        } catch (error) {
            console.error("Error adding duplicate events:", error);
            setError("An error occurred while adding duplicate events. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, [selectedDuplicates, onDuplicateSelection, hasImportableEvents, importState]);

    const handleClose = useCallback(
        (event: {} | MouseEvent<HTMLElement>, reason: EUiModalReason) => {
            onClose?.(event, reason);

            if (
                reason === EUiModalReason.TryAgainBtnClick ||
                reason === EUiModalReason.Import ||
                reason === EUiModalReason.CloseIconClick
            ) {
                setImportState(null);
                setError(undefined);
                setSelectedDuplicates([]);
                setHasImportableEvents(false);
                // reload the page to update the list of events
                navigate(0);
            }

            if (reason === EUiModalReason.EscapeKeyDown || reason === EUiModalReason.CloseIconClick) {
                setImportState(null);
                setError(undefined);
                setSelectedDuplicates([]);
                setHasImportableEvents(false);
            }
        },
        [onClose, navigate]
    );

    const alertProps = getAlertProps(importState?.response ?? null);
    const footerStatusResponse = importState?.response.status ?? null;

    return (
        <UiModal
            title="Import LV1 events only"
            open={open}
            onClose={handleClose}
            footerButtons={[
                <FooterModalImportLVL1
                    key="footer"
                    statusResponse={footerStatusResponse}
                    onClose={handleClose}
                    setImportResponse={setImportState}
                    selectedDuplicates={selectedDuplicates}
                    onDuplicateSubmit={handleDuplicateSubmit}
                    isLoading={isLoading}
                    importState={importState}
                    hasImportableEvents={hasImportableEvents}
                />,
            ]}
            className="modal-import-lvl1"
            {...rest}
        >
            {alertProps ? <StatusAlert {...alertProps} /> : null}

            {!importState ? (
                <>
                    <section>
                        <p>
                            This feature enables to import a batch of LV1 events. To use it, upload an Excel file.
                            Please download and use the provided template.
                        </p>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<GetAppIcon />}
                            className="modal-import-lvl1__download-button"
                            href="/documents/template_events_import.xlsx"
                        >
                            Download Template (.xls)
                        </Button>
                    </section>
                    <DragAndDrop
                        onFileDrop={handleImport}
                        accept=".xls,.xlsx"
                        externalLoading={isLoading}
                        externalError={error}
                        onLoadingChange={setIsLoading}
                        onErrorChange={setError}
                        inputRef={fileInputRef}
                    />
                </>
            ) : importState.type === "import" ? (
                <ImportedDataDisplay
                    data={importState.response}
                    onDuplicateSelection={handleDuplicateSelectionChange}
                />
            ) : null}
        </UiModal>
    );
};

export default ModalImportLVL1;
